import { languageDisplayLabel, LanguagesEnum } from '@/api/enums';
import { GlobeIcon } from '@/utils/helpers';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const StyledContainer = styled.div`
  position: absolute;
  top: var(--small-padding);
  right: var(--small-padding);
  z-index: 5;
`;

const StyledToggle = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid var(--gray-dark);
  background: var(--white-main);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: background 0.2s ease;

  &:hover {
    background: var(--beige-main);
  }
`;

const StyledDropdown = styled.ul`
  position: absolute;
  top: 40px;
  right: 0;
  min-width: 200px;
  max-height: 240px;
  overflow-y: auto;
  background: var(--white-main);
  border: 1px solid var(--gray-dark);
  border-radius: var(--default-border-radius);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  list-style: none;
  margin: 0;
  padding: var(--xxsmall-padding) 0;
`;

const StyledOption = styled.li<{ $active: boolean }>`
  padding: var(--xxsmall-padding) var(--small-padding);
  cursor: pointer;
  font-size: var(--small-font-size);
  background: ${({ $active }) =>
    $active ? 'var(--beige-main)' : 'transparent'};
  font-weight: ${({ $active }) => ($active ? '600' : '400')};

  &:hover {
    background: var(--beige-main);
  }
`;

type PreviewLanguageSelectorProps = {
  languages: LanguagesEnum[];
  selectedLanguage: LanguagesEnum;
  onLanguageChange: (language: LanguagesEnum) => void;
};

export const PreviewLanguageSelector: React.FC<
  PreviewLanguageSelectorProps
> = ({ languages, selectedLanguage, onLanguageChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (languages.length <= 1) return null;

  return (
    <StyledContainer ref={containerRef}>
      {isOpen && (
        <StyledDropdown>
          {languages.map((lang) => (
            <StyledOption
              key={lang}
              $active={lang === selectedLanguage}
              onClick={() => {
                onLanguageChange(lang);
                setIsOpen(false);
              }}
            >
              {languageDisplayLabel[lang] || lang}
            </StyledOption>
          ))}
        </StyledDropdown>
      )}
      <StyledToggle onClick={() => setIsOpen(!isOpen)}>
        <GlobeIcon />
      </StyledToggle>
    </StyledContainer>
  );
};
